/**
 * CSAF → Sandvik-branded advisory PDF (print) generator.
 *
 * Builds a self-contained HTML rendering of a CSAF 2.0 document that matches
 * the prescribed Sandvik "Cybersecurity Advisory" layout, then prints it via a
 * hidden iframe so the browser's native "Save as PDF" produces high-quality,
 * selectable, vector output. Zero runtime dependencies and no backend changes.
 *
 * The layout, section order and branding follow the approved template. The
 * brand wordmark/colours are centralised in BRAND so a real logo asset can be
 * dropped in later without touching the mapping logic.
 */

type Json = Record<string, unknown>;

import { SANDVIK_LOGO_SVG } from "../assets/sandvikLogo";

const BRAND = {
  wordmark: "SANDVIK",
  headerColor: "#8a7d55", // muted gold used for table headers in the template
  copyrightHolder: "Sandvik AB",
};

// ---------------------------------------------------------------------------
// Safe accessors — stored advisories may be partial, so never throw.
// ---------------------------------------------------------------------------
const asObj = (v: unknown): Json =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Json) : {};
const asArr = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const asStr = (v: unknown): string => (typeof v === "string" ? v : "");
const asNum = (v: unknown): number | undefined =>
  typeof v === "number" ? v : undefined;

function esc(value: unknown): string {
  return asStr(typeof value === "string" ? value : String(value ?? ""))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function severityLabel(score: number | undefined): string {
  if (score === undefined) return "";
  if (score >= 9) return "Critical";
  if (score >= 7) return "High";
  if (score >= 4) return "Moderate";
  if (score > 0) return "Low";
  return "None";
}

// ---------------------------------------------------------------------------
// product_tree → { productId: fullName } resolution (recursive branches +
// full_product_names + relationships).
// ---------------------------------------------------------------------------
function collectProductNames(csaf: Json): Map<string, string> {
  const names = new Map<string, string>();

  const visitBranch = (branch: Json) => {
    const product = asObj(branch.product);
    const id = asStr(product.product_id);
    const name = asStr(product.name) || asStr(branch.name);
    if (id) names.set(id, name);
    for (const child of asArr(branch.branches)) visitBranch(asObj(child));
  };

  const tree = asObj(csaf.product_tree);
  for (const branch of asArr(tree.branches)) visitBranch(asObj(branch));
  for (const fpn of asArr(tree.full_product_names)) {
    const p = asObj(fpn);
    const id = asStr(p.product_id);
    if (id) names.set(id, asStr(p.name));
  }
  for (const rel of asArr(tree.relationships)) {
    const r = asObj(rel);
    const full = asObj(r.full_product_name);
    const id = asStr(full.product_id);
    if (id) names.set(id, asStr(full.name));
  }
  return names;
}

function productName(id: string, names: Map<string, string>): string {
  return names.get(id) || id;
}

// ---------------------------------------------------------------------------
// CVSS helpers
// ---------------------------------------------------------------------------
interface CvssInfo {
  version: string;
  baseScore: number | undefined;
  vector: string;
}

function extractCvss(vuln: Json): CvssInfo {
  for (const score of asArr(vuln.scores)) {
    const s = asObj(score);
    const v31 = asObj(s.cvss_v3);
    if (Object.keys(v31).length > 0) {
      return {
        version: asStr(v31.version) || "3.x",
        baseScore: asNum(v31.baseScore),
        vector: asStr(v31.vectorString),
      };
    }
    const v2 = asObj(s.cvss_v2);
    if (Object.keys(v2).length > 0) {
      return {
        version: "2.0",
        baseScore: asNum(v2.baseScore),
        vector: asStr(v2.vectorString),
      };
    }
  }
  return { version: "", baseScore: undefined, vector: "" };
}

function topCvss(csaf: Json): CvssInfo {
  let best: CvssInfo = { version: "", baseScore: undefined, vector: "" };
  for (const vuln of asArr(csaf.vulnerabilities)) {
    const info = extractCvss(asObj(vuln));
    if (
      info.baseScore !== undefined &&
      (best.baseScore === undefined || info.baseScore > best.baseScore)
    ) {
      best = info;
    }
  }
  return best;
}

// ---------------------------------------------------------------------------
// Section renderers
// ---------------------------------------------------------------------------
const STATUS_LABELS: Record<string, string> = {
  first_affected: "First affected",
  known_affected: "Known affected",
  known_not_affected: "Known not affected",
  first_fixed: "First fixed",
  fixed: "Fixed",
  recommended: "Recommended",
  last_affected: "Last affected",
  under_investigation: "Under investigation",
};

function row(label: string, value: string): string {
  if (!value) return "";
  return `<tr><td class="k">${esc(label)}</td><td class="v">${value}</td></tr>`;
}

function metaBlock(csaf: Json): string {
  const doc = asObj(csaf.document);
  const tracking = asObj(doc.tracking);
  const publisher = asObj(doc.publisher);
  const generator = asObj(tracking.generator);
  const engine = asObj(generator.engine);
  const cvss = topCvss(csaf);

  const aliases = asArr(tracking.aliases).map(asStr).filter(Boolean).join(", ");
  let canonical = "";
  for (const ref of asArr(doc.references)) {
    const r = asObj(ref);
    if (asStr(r.category) === "self") {
      canonical = `<a href="${esc(r.url)}">${esc(r.url)}</a>`;
      break;
    }
  }

  const publisherLines = [
    esc(publisher.name),
    publisher.namespace
      ? `<a href="${esc(publisher.namespace)}">${esc(publisher.namespace)}</a>`
      : "",
    esc(publisher.contact_details).replace(/\n/g, "<br/>"),
  ]
    .filter(Boolean)
    .join("<br/>");

  let vulnReports = "";
  for (const ref of asArr(doc.references)) {
    const r = asObj(ref);
    if (
      asStr(r.category) === "external" &&
      /report|psirt|security/i.test(asStr(r.summary))
    ) {
      vulnReports = `<a href="${esc(r.url)}">${esc(r.url)}</a>`;
      break;
    }
  }

  return `<table class="meta">
    ${row("Publisher:", publisherLines)}
    ${row("Category:", esc(publisher.category))}
    ${row("Issuing authority:", esc(publisher.issuing_authority).replace(/\n/g, "<br/>"))}
    ${row("Vulnerability reports:", vulnReports)}
    ${row("Initial release date:", esc(tracking.initial_release_date))}
    ${row("Current release date:", esc(tracking.current_release_date))}
    ${row("Build date:", esc(generator.date))}
    ${row("Current version:", esc(tracking.version))}
    ${row("Status:", esc(tracking.status))}
    ${row(`CVSSv${cvss.version || "3.1"} base score:`, cvss.baseScore !== undefined ? esc(cvss.baseScore) : "")}
    ${row("Severity:", esc(severityLabel(cvss.baseScore)))}
    ${row("Engine:", engine.name ? esc(`${asStr(engine.name)} ${asStr(engine.version)}`) : "")}
    ${row("Also referred to:", esc(aliases))}
    ${row("Canonical CSAF file:", canonical)}
  </table>`;
}

function notesSections(notes: unknown[]): string {
  return asArr(notes)
    .map((note) => {
      const n = asObj(note);
      const title = asStr(n.title) || asStr(n.category) || "Note";
      const text = esc(n.text).replace(/\n/g, "<br/>");
      if (!text) return "";
      return `<section class="block">
        <h2>${esc(title)}</h2>
        <p>${text}</p>
      </section>`;
    })
    .join("");
}

function vulnerabilityDetails(csaf: Json): string {
  const vulns = asArr(csaf.vulnerabilities);
  if (vulns.length === 0) return "";
  const items = vulns
    .map((vuln) => {
      const v = asObj(vuln);
      const cve = asStr(v.cve);
      const cwe = asObj(v.cwe);
      const cweLine = cwe.id ? `${asStr(cwe.id)} - ${asStr(cwe.name)}` : "";
      const cvss = extractCvss(v);
      const description = asArr(v.notes)
        .map(asObj)
        .filter((n) => asStr(n.category) === "description")
        .map((n) => esc(n.text))
        .join("<br/>");
      const exploitation = asArr(v.threats)
        .map(asObj)
        .filter((t) => asStr(t.category) === "exploit_status")
        .map((t) => esc(t.details))
        .join("<br/>");

      const idsRows = asArr(v.ids)
        .map((idObj) => {
          const id = asObj(idObj);
          return `<tr><td>${esc(id.system_name)}</td><td>${esc(id.text)}</td></tr>`;
        })
        .join("");
      const idsTable = idsRows
        ? `<table class="grid"><thead><tr><th>System</th><th>Identifier</th></tr></thead><tbody>${idsRows}</tbody></table>`
        : "";

      const involvementRows = asArr(v.involvements)
        .map((inv) => {
          const i = asObj(inv);
          return `<tr><td>${esc(i.date)}</td><td>${esc(i.party)}</td><td>${esc(i.status)}</td><td>${esc(i.summary)}</td></tr>`;
        })
        .join("");
      const involvementTable = involvementRows
        ? `<table class="grid"><thead><tr><th>Date</th><th>Party</th><th>Status</th><th>Summary</th></tr></thead><tbody>${involvementRows}</tbody></table>`
        : "";

      return `<div class="vuln">
        <h3>Vulnerability: ${esc(cve) || "(no CVE)"}</h3>
        ${cweLine ? `<p class="cwe">${esc(cweLine)}</p>` : ""}
        ${row2("Title", esc(v.title))}
        ${row2("Weakness (CWE)", esc(cweLine))}
        ${row2("Discovered", esc(v.discovery_date))}
        ${row2("Disclosed", esc(v.release_date))}
        ${row2(`Severity (CVSS v${cvss.version || "3.1"})`, cvss.baseScore !== undefined ? `Base score ${esc(cvss.baseScore)} (${esc(severityLabel(cvss.baseScore))})` : "")}
        ${row2("Vector", esc(cvss.vector))}
        ${row2("Description", description)}
        ${row2("Exploitation status", exploitation)}
        ${idsTable ? `<p class="field"><span class="fl">Other identifiers</span></p>${idsTable}` : ""}
        ${involvementTable ? `<p class="field"><span class="fl">Disclosure &amp; involvements</span></p>${involvementTable}` : ""}
      </div>`;
    })
    .join("");
  return `<section class="block">
    <h2>Vulnerability details</h2>
    ${items}
  </section>`;
}

function row2(label: string, value: string): string {
  if (!value) return "";
  return `<p class="field"><span class="fl">${esc(label)}</span><br/>${value}</p>`;
}

function remediationsSection(csaf: Json, names: Map<string, string>): string {
  const rows: string[] = [];
  for (const vuln of asArr(csaf.vulnerabilities)) {
    for (const rem of asArr(asObj(vuln).remediations)) {
      const r = asObj(rem);
      const appliesTo = asArr(r.product_ids)
        .map((id) => productName(asStr(id), names))
        .join(", ");
      const target = asStr(r.url)
        ? `<a href="${esc(r.url)}">${esc(r.url)}</a>`
        : esc(appliesTo);
      const restart = asObj(r.restart_required);
      const restartText = [asStr(restart.category), asStr(restart.details)]
        .filter(Boolean)
        .join(" — ");
      rows.push(
        `<tr><td>${esc(r.category)}</td><td>${esc(r.details)}</td><td>${esc(restartText)}</td><td>${target}</td></tr>`,
      );
    }
  }
  if (rows.length === 0) return "";
  return `<section class="block">
    <h2>Mitigations and Remediations</h2>
    <table class="grid">
      <thead><tr><th>Type</th><th>Action</th><th>Restart</th><th>Applies to / URL</th></tr></thead>
      <tbody>${rows.join("")}</tbody>
    </table>
  </section>`;
}

function acknowledgmentsSection(csaf: Json): string {
  const doc = asObj(csaf.document);
  const acks: Json[] = [];
  for (const a of asArr(doc.acknowledgments)) acks.push(asObj(a));
  for (const vuln of asArr(csaf.vulnerabilities)) {
    for (const a of asArr(asObj(vuln).acknowledgments)) acks.push(asObj(a));
  }
  if (acks.length === 0) return "";
  const rows = acks
    .map((a) => {
      const who = asArr(a.names).map(asStr).filter(Boolean).join(", ");
      const org = asStr(a.organization);
      const urls = asArr(a.urls)
        .map(asStr)
        .filter(Boolean)
        .map((u) => `<a href="${esc(u)}">${esc(u)}</a>`)
        .join("<br/>");
      const summary = asStr(a.summary);
      return `<tr><td>${esc(who)}</td><td>${esc(org)}</td><td>${urls}</td><td>${esc(summary)}</td></tr>`;
    })
    .join("");
  return `<section class="block">
    <h2>Acknowledgements</h2>
    <table class="grid">
      <thead><tr><th>Name</th><th>Organization</th><th>URLs</th><th>Summary</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </section>`;
}

function revisionHistorySection(csaf: Json): string {
  const tracking = asObj(asObj(csaf.document).tracking);
  const rows = asArr(tracking.revision_history)
    .map((rev) => {
      const r = asObj(rev);
      return `<tr><td>${esc(r.number)}</td><td>${esc(r.date)}</td><td>${esc(r.summary)}</td></tr>`;
    })
    .join("");
  if (!rows) return "";
  return `<section class="block">
    <h2>Revision history</h2>
    <table class="grid">
      <thead><tr><th>Version</th><th>Date of the revision</th><th>Summary of the revision</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </section>`;
}

// ---------------------------------------------------------------------------
// Products, relationships, product groups (product_tree)
// ---------------------------------------------------------------------------
function identificationHelper(product: Json): string {
  const helper = asObj(product.product_identification_helper);
  const lines: string[] = [];
  const add = (label: string, value: string) => {
    if (value)
      lines.push(`<div><strong>${esc(label)}:</strong> ${esc(value)}</div>`);
  };
  add("CPE", asStr(helper.cpe));
  add(
    "Models",
    asArr(helper.model_numbers).map(asStr).filter(Boolean).join(", "),
  );
  add(
    "Serials",
    asArr(helper.serial_numbers).map(asStr).filter(Boolean).join(", "),
  );
  add("SKUs", asArr(helper.skus).map(asStr).filter(Boolean).join(", "));
  add("SBOM", asArr(helper.sbom_urls).map(asStr).filter(Boolean).join(", "));
  const hashes = asArr(helper.hashes)
    .map((h) =>
      asArr(asObj(h).file_hashes)
        .map((fh) => {
          const f = asObj(fh);
          return `${asStr(f.algorithm)}:${asStr(f.value)}`;
        })
        .join(", "),
    )
    .filter(Boolean)
    .join("; ");
  add("Hash", hashes);
  add("PURL", asStr(helper.purl));
  add(
    "URI",
    asArr(helper.x_generic_uris)
      .map((u) => asStr(asObj(u).uri))
      .filter(Boolean)
      .join(", "),
  );
  return lines.join("");
}

function collectProducts(csaf: Json): Json[] {
  const out: Json[] = [];
  const seen = new Set<string>();
  const pushProduct = (p: Json) => {
    const id = asStr(p.product_id);
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(p);
  };
  const visitBranch = (branch: Json) => {
    const product = asObj(branch.product);
    if (asStr(product.product_id)) pushProduct(product);
    for (const child of asArr(branch.branches)) visitBranch(asObj(child));
  };
  const tree = asObj(csaf.product_tree);
  for (const branch of asArr(tree.branches)) visitBranch(asObj(branch));
  for (const fpn of asArr(tree.full_product_names)) pushProduct(asObj(fpn));
  for (const rel of asArr(tree.relationships))
    pushProduct(asObj(asObj(rel).full_product_name));
  return out;
}

function productsSection(csaf: Json): string {
  const products = collectProducts(csaf);
  if (products.length === 0) return "";
  const rows = products
    .map((p) => {
      const helper = identificationHelper(p) || "&nbsp;";
      return `<tr><td>${esc(p.product_id)}</td><td>${esc(p.name)}</td><td>${helper}</td></tr>`;
    })
    .join("");
  return `<section class="block">
    <h2>Product information</h2>
    <table class="grid">
      <thead><tr><th>Product ID</th><th>Full product name</th><th>Identification helper</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </section>`;
}

const RELATIONSHIP_LABELS: Record<string, string> = {
  default_component_of: "Default component of",
  external_component_of: "External component of",
  installed_on: "Installed on",
  installed_with: "Installed with",
  optional_component_of: "Optional component of",
};

function relationshipsSection(csaf: Json, names: Map<string, string>): string {
  const tree = asObj(csaf.product_tree);
  const rows = asArr(tree.relationships)
    .map((rel) => {
      const r = asObj(rel);
      const full = asObj(r.full_product_name);
      const category = asStr(r.category);
      const label = RELATIONSHIP_LABELS[category] || category;
      const component = productName(asStr(r.product_reference), names);
      const relatesTo = productName(
        asStr(r.relates_to_product_reference),
        names,
      );
      return `<tr><td>${esc(full.product_id)}</td><td>${esc(label)}</td><td>${esc(component)}</td><td>${esc(relatesTo)}</td></tr>`;
    })
    .join("");
  if (!rows) return "";
  return `<section class="block">
    <h2>Relationships</h2>
    <table class="grid">
      <thead><tr><th>Resulting product (ID)</th><th>Relationship</th><th>Component</th><th>Relates to</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </section>`;
}

function productGroupsSection(csaf: Json, names: Map<string, string>): string {
  const tree = asObj(csaf.product_tree);
  const rows = asArr(tree.product_groups)
    .map((grp) => {
      const g = asObj(grp);
      const members = asArr(g.product_ids)
        .map((id) => productName(asStr(id), names))
        .join(", ");
      return `<tr><td>${esc(g.group_id)}</td><td>${esc(g.summary)}</td><td>${esc(members)}</td></tr>`;
    })
    .join("");
  if (!rows) return "";
  return `<section class="block">
    <h2>Product groups</h2>
    <table class="grid">
      <thead><tr><th>Group ID</th><th>Summary</th><th>Members</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
  </section>`;
}

// ---------------------------------------------------------------------------
// Threats & references
// ---------------------------------------------------------------------------
const THREAT_LABELS: Record<string, string> = {
  exploit_status: "Exploit status",
  impact: "Impact",
  target_set: "Target set",
};

function threatsSection(csaf: Json, names: Map<string, string>): string {
  const rows: string[] = [];
  for (const vuln of asArr(csaf.vulnerabilities)) {
    for (const threat of asArr(asObj(vuln).threats)) {
      const t = asObj(threat);
      const category = asStr(t.category);
      const label = THREAT_LABELS[category] || category;
      const appliesTo = asArr(t.product_ids)
        .map((id) => productName(asStr(id), names))
        .join(", ");
      rows.push(
        `<tr><td>${esc(label)}</td><td>${esc(t.date)}</td><td>${esc(t.details)}</td><td>${esc(appliesTo)}</td></tr>`,
      );
    }
  }
  if (rows.length === 0) return "";
  return `<section class="block">
    <h2>Threats</h2>
    <table class="grid">
      <thead><tr><th>Category</th><th>Date</th><th>Details</th><th>Applies to</th></tr></thead>
      <tbody>${rows.join("")}</tbody>
    </table>
  </section>`;
}

const REFERENCE_LABELS: Record<string, string> = {
  self: "Self",
  external: "External",
};

function referencesSection(csaf: Json): string {
  const doc = asObj(csaf.document);
  const rows: string[] = [];
  const addRefs = (refs: unknown[]) => {
    for (const ref of asArr(refs)) {
      const r = asObj(ref);
      const category = asStr(r.category);
      const label = REFERENCE_LABELS[category] || category || "external";
      const url = asStr(r.url)
        ? `<a href="${esc(r.url)}">${esc(r.url)}</a>`
        : "";
      rows.push(
        `<tr><td>${esc(label)}</td><td>${esc(r.summary)}</td><td>${url}</td></tr>`,
      );
    }
  };
  addRefs(asArr(doc.references));
  for (const vuln of asArr(csaf.vulnerabilities))
    addRefs(asArr(asObj(vuln).references));
  if (rows.length === 0) return "";
  return `<section class="block">
    <h2>References</h2>
    <table class="grid">
      <thead><tr><th>Category</th><th>Summary</th><th>URL</th></tr></thead>
      <tbody>${rows.join("")}</tbody>
    </table>
  </section>`;
}

// ---------------------------------------------------------------------------
// Product status (per state) & justifications for non-affected (flags)
// ---------------------------------------------------------------------------
function productStatusSection(csaf: Json, names: Map<string, string>): string {
  const rows: string[] = [];
  for (const [bucket, label] of Object.entries(STATUS_LABELS)) {
    const products: string[] = [];
    const seen = new Set<string>();
    for (const vuln of asArr(csaf.vulnerabilities)) {
      const status = asObj(asObj(vuln).product_status);
      for (const pid of asArr(status[bucket])) {
        const id = asStr(pid);
        if (seen.has(id)) continue;
        seen.add(id);
        products.push(productName(id, names));
      }
    }
    if (products.length > 0) {
      rows.push(
        `<tr><td>${esc(label)}</td><td>${esc(products.join(", "))}</td></tr>`,
      );
    }
  }
  if (rows.length === 0) return "";
  return `<section class="block">
    <h2>Product status</h2>
    <table class="grid">
      <thead><tr><th>Status</th><th>Products</th></tr></thead>
      <tbody>${rows.join("")}</tbody>
    </table>
  </section>`;
}

function justificationsSection(csaf: Json, names: Map<string, string>): string {
  const rows: string[] = [];
  for (const vuln of asArr(csaf.vulnerabilities)) {
    for (const flag of asArr(asObj(vuln).flags)) {
      const f = asObj(flag);
      const appliesTo = asArr(f.product_ids)
        .map((id) => productName(asStr(id), names))
        .join(", ");
      rows.push(
        `<tr><td>${esc(f.label)}</td><td>${esc(f.date)}</td><td>${esc(appliesTo)}</td></tr>`,
      );
    }
  }
  if (rows.length === 0) return "";
  return `<section class="block">
    <h2>Justifications for non-affected products</h2>
    <table class="grid">
      <thead><tr><th>Label</th><th>Date</th><th>Applies to</th></tr></thead>
      <tbody>${rows.join("")}</tbody>
    </table>
  </section>`;
}

// ---------------------------------------------------------------------------
// Full document assembly
// ---------------------------------------------------------------------------
export function buildAdvisoryHtml(csaf: Json): string {
  const doc = asObj(csaf.document);
  const tracking = asObj(doc.tracking);
  const distribution = asObj(doc.distribution);
  const tlp = asObj(distribution.tlp);
  const names = collectProductNames(csaf);
  const year = new Date().getFullYear();

  const cves = asArr(csaf.vulnerabilities)
    .map((v) => asStr(asObj(v).cve))
    .filter(Boolean);

  const documentId = asStr(tracking.id);
  const tlpLabel = asStr(tlp.label) || "WHITE";
  const category = asStr(doc.category);
  const generator = asObj(tracking.generator);
  const engine = asObj(generator.engine);
  const engineLine = engine.name
    ? `${asStr(engine.name)} ${asStr(engine.version)}`.trim()
    : "";
  const csafVersion = asStr(csaf.csaf_version) || asStr(doc.csaf_version);
  const aliases = asArr(tracking.aliases).map(asStr).filter(Boolean).join(", ");
  const distributionText = asStr(distribution.text);

  const disclaimer = asArr(doc.notes)
    .map(asObj)
    .find((n) => asStr(n.category) === "legal_disclaimer");
  const disclaimerText = disclaimer
    ? esc(disclaimer.text).replace(/\n/g, "<br/>")
    : "This advisory is provided on an 'as is' basis and does not imply any kind of guarantee or warranty. Your use of the information in this advisory is at your own risk.";

  const narrativeNotes = asArr(doc.notes)
    .map(asObj)
    .filter((n) => asStr(n.category) !== "legal_disclaimer");

  return `<!doctype html>
<html lang="${esc(doc.lang) || "en-US"}">
<head>
<meta charset="utf-8"/>
<title>${esc(doc.title) || "Security Advisory"}</title>
<style>
  @page { size: A4; margin: 28mm 16mm 22mm 16mm; }
  * { box-sizing: border-box; }
  body { font-family: Arial, Helvetica, sans-serif; color: #1a1a1a; font-size: 11px; line-height: 1.45; margin: 0; }
  a { color: #1155cc; text-decoration: none; word-break: break-all; }

  .brandbar { position: fixed; top: 0; left: 0; right: 0; height: 20mm; padding: 6mm 16mm 0;
    display: flex; justify-content: space-between; align-items: flex-start; }
  .brandmark { display: flex; align-items: center; gap: 8px; }
  .brandmark .logo { display: inline-flex; width: 30px; height: 30px; color: #111; }
  .brandmark .logo svg { display: block; width: 100%; height: 100%; }
  .wordmark { font-weight: 800; font-size: 18px; letter-spacing: 3px; color: #111; }
  .brandbar .right { text-align: left; font-size: 10px; color: #333; }
  .footbar { position: fixed; bottom: 0; left: 0; right: 0; height: 14mm; padding: 0 16mm;
    display: flex; align-items: center; color: #8a8a8a; font-size: 9px;
    border-top: 1px solid #e2e2e2; }

  .doc-meta { text-align: right; color: #444; font-size: 10px; margin: 0 0 6px; }
  .doc-meta div { margin: 1px 0; }
  .adv-label { letter-spacing: 2px; font-size: 12px; color: #333; margin-top: 4px; }
  h1.title { font-size: 26px; line-height: 1.2; margin: 4px 0 8px; color: #111; }
  .cves { font-size: 15px; font-weight: 700; margin: 0 0 14px; }
  .cves span { display: block; }

  table.meta { border-collapse: collapse; width: 100%; margin: 6px 0 4px; }
  table.meta td { vertical-align: top; padding: 2px 6px 2px 0; }
  table.meta td.k { width: 170px; color: #333; font-weight: 600; }
  table.meta td.v { color: #111; }

  section.block { margin: 14px 0; page-break-inside: avoid; }
  section.block > h2 { font-size: 18px; margin: 0 0 6px; border-top: 1px solid #cfcfcf; padding-top: 10px; color: #111; }
  section.block p { margin: 4px 0; }

  table.grid { border-collapse: collapse; width: 100%; margin: 6px 0; }
  table.grid th { background: ${BRAND.headerColor}; color: #fff; text-align: left;
    padding: 5px 8px; font-size: 11px; }
  table.grid td { padding: 5px 8px; border-bottom: 1px solid ${BRAND.headerColor}55; }

  .vuln { margin: 10px 0; page-break-inside: avoid; }
  .vuln h3 { font-size: 15px; margin: 6px 0 2px; }
  .vuln .cwe { color: #555; margin: 0 0 6px; }
  .field { margin: 6px 0; }
  .field .fl { font-weight: 700; }
</style>
</head>
<body>
  <div class="brandbar">
    <div class="brandmark">
      <span class="logo">${SANDVIK_LOGO_SVG}</span>
      <span class="wordmark">${esc(BRAND.wordmark)}</span>
    </div>
    <div class="right">
      <div>TLP: ${esc(tlpLabel)}</div>
      <div>Document ID: ${esc(documentId)}</div>
    </div>
  </div>

  <main>
    <div class="doc-meta">
      ${category ? `<div>Document category: ${esc(category)}</div>` : ""}
      ${csafVersion ? `<div>CSAF version: ${esc(csafVersion)}</div>` : ""}
      ${engineLine ? `<div>Engine: ${esc(engineLine)}</div>` : ""}
      ${doc.source_lang ? `<div>Original language: ${esc(doc.source_lang)}</div>` : ""}
      ${doc.lang ? `<div>Language: ${esc(doc.lang)}</div>` : ""}
      ${aliases ? `<div>Aliases: ${esc(aliases)}</div>` : ""}
      ${distributionText ? `<div>Distribution: ${esc(distributionText)}</div>` : ""}
    </div>

    <div class="adv-label">CYBERSECURITY ADVISORY</div>
    <h1 class="title">${esc(doc.title) || "Untitled advisory"}</h1>
    ${cves.length ? `<div class="cves">${cves.map((c) => `<span>${esc(c)}</span>`).join("")}</div>` : ""}

    ${metaBlock(csaf)}

    ${notesSections(narrativeNotes)}

    ${productsSection(csaf)}

    ${relationshipsSection(csaf, names)}

    ${productGroupsSection(csaf, names)}

    ${vulnerabilityDetails(csaf)}

    ${productStatusSection(csaf, names)}

    ${justificationsSection(csaf, names)}

    ${remediationsSection(csaf, names)}

    ${threatsSection(csaf, names)}

    ${acknowledgmentsSection(csaf)}

    ${referencesSection(csaf)}

    ${revisionHistorySection(csaf)}

    <section class="block">
      <h2>Disclaimer</h2>
      <p>${disclaimerText}</p>
    </section>
  </main>

  <div class="footbar">© Copyright ${year} ${esc(BRAND.copyrightHolder)}. All rights reserved.</div>
</body>
</html>`;
}

/**
 * Render the CSAF document to the Sandvik advisory layout and open the
 * browser print dialog (Save as PDF) via a hidden iframe. Returns false if the
 * iframe could not be created.
 */
export function printCsafPdf(csaf: unknown): boolean {
  const html = buildAdvisoryHtml(asObj(csaf));
  const iframe = document.createElement("iframe");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.position = "fixed";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  document.body.appendChild(iframe);

  const win = iframe.contentWindow;
  if (!win) {
    iframe.remove();
    return false;
  }

  const cleanup = () => {
    window.setTimeout(() => iframe.remove(), 1000);
  };

  iframe.onload = () => {
    win.focus();
    win.print();
    if ("onafterprint" in win) {
      win.onafterprint = cleanup;
    } else {
      cleanup();
    }
  };

  const idoc = iframe.contentDocument;
  if (!idoc) {
    iframe.remove();
    return false;
  }
  idoc.open();
  idoc.write(html);
  idoc.close();
  return true;
}
